import type { BlogBlock } from '~/types/blog'
import { stripInlineMarkup } from './inline'

function joinItems(items: Array<string | undefined>): string {
  return items.filter(Boolean).join(' ')
}

export function blockPlainText(block: BlogBlock): string {
  switch (block.type) {
    case 'paragraph':
    case 'heading':
    case 'quote':
    case 'doctorNote':
    case 'disclaimer':
    case 'medicalAlert':
      return stripInlineMarkup(block.text || '')
    case 'list':
    case 'keyPoints':
    case 'symptomChecklist':
    case 'doNotList':
      return joinItems((block.items ?? []).map((item) => stripInlineMarkup(item)))
    case 'faq':
      return joinItems((block.items ?? []).flatMap((item) => [item.question, item.answer]))
    case 'steps':
      return joinItems((block.items ?? []).flatMap((item) => [item.title, item.text]))
    case 'references':
      return joinItems((block.items ?? []).map((item) => item.title))
    case 'table':
      return joinItems([...(block.headers ?? []), ...(block.rows ?? []).flat()])
    case 'cta':
      return joinItems([block.title, block.description, block.buttonLabel])
    case 'link':
      return block.label
    case 'image':
      return joinItems([block.alt, block.caption])
    case 'video':
    case 'audio':
      return block.caption || ''
    case 'gallery':
      return joinItems((block.items ?? []).map((item) => item.alt))
    case 'beforeAfter':
      return ''
    case 'quiz':
      return block.quizTitle || ''
    case 'survey':
      return block.surveyTitle || ''
    case 'divider':
      return ''
  }
}

export function articlePlainText(blocks: BlogBlock[]): string {
  if (!Array.isArray(blocks)) return ''
  return blocks.map(blockPlainText).filter(Boolean).join('\n')
}

export function headingOutline(blocks: BlogBlock[]): Array<{ id: string; text: string; level: 2 | 3 }> {
  if (!Array.isArray(blocks)) return []
  return blocks
    .filter((block): block is Extract<BlogBlock, { type: 'heading' }> => block.type === 'heading' && Boolean(block.text?.trim()))
    .map((block) => ({ id: block.id, text: stripInlineMarkup(block.text), level: block.level }))
}
